import React, { useState, useEffect } from "react";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { IoCloseCircleOutline, IoCloudUploadOutline } from "react-icons/io5";
import { FiSave } from "react-icons/fi";

const UpdateBannerModal = ({ isOpen, onClose, fetchBanners, initialData }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    _id: "",
    type: "top",
    image: "",
  });

  // Populate Form Data
  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        _id: initialData._id || "",
        type: initialData.type || "top",
        image: initialData.image || "",
      });
    }
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const result = await uploadFiles({
        files: [file],
        endpoint: "/uploadImage",
        fieldName: "file",
        isMultiple: false,
      });

      const imageUrl = result?.data?.imageUrl;
      if (imageUrl) {
        setFormData((prev) => ({ ...prev, image: imageUrl }));
        showSuccessToast("Image uploaded successfully!");
      } else {
        showErrorToast("Image upload failed");
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      showErrorToast("Error uploading image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const response = await makeRequestAuth("post", "/admin/updateBanner", {
        id: formData._id,
        type: formData.type,
        image: formData.image,
      });

      if (response.error || response.status === false) {
        showErrorToast(response.message || "Failed to update banner");
        return;
      }

      showSuccessToast(response.message || "Banner updated successfully");
      fetchBanners(); // Refresh list
      onClose(); // Close modal
    } catch (error) {
      console.error("Error updating banner:", error);
      showErrorToast(error.message || "Failed to update banner");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col border border-purple-300 dark:border-purple-600/50 animate-fadeIn">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-2xl">
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Edit Banner
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition"
          >
            <IoCloseCircleOutline size={28} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 space-y-6">
          {/* Banner Type Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Banner Type
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="top">Top Banner</option>
              <option value="bottom">Bottom Banner</option>
            </select>
          </div>

          {/* Image Upload Area */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Banner Image
            </label>
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 bg-gray-50 dark:bg-gray-800/50 relative transition-colors hover:bg-gray-100 dark:hover:bg-gray-700/50">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={isUploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              {isUploading ? (
                <div className="text-gray-500 font-medium">Uploading...</div>
              ) : formData.image ? (
                <div className="flex flex-col items-center">
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="h-32 w-auto object-cover rounded shadow-md mb-3 border border-gray-200 dark:border-gray-600"
                  />
                  <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                    Click or drag to replace
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                  <IoCloudUploadOutline size={48} className="mb-2 opacity-50" />
                  <p className="font-medium">Click to upload image</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading || !formData.image}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-[1.02] text-white font-semibold shadow-lg transition disabled:opacity-50"
          >
            {isSubmitting ? (
              "Saving..."
            ) : (
              <>
                <FiSave /> Update Banner
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBannerModal;
